import { css } from '@linaria/core';

import type { CalculatedColumn, SortInfo, ISelectorEvent } from './types';
import type { HeaderRowProps } from './HeaderRow';
import SortableHeaderCell from './headerCells/SortableHeaderCell';
import { getCellStyle, getCellClassname, isCtrlDown } from './utils';
import { useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useCombinedRefs } from './hooks/useCombinedRefs';
import { SELECT_COLUMN_KEY } from './Columns';
import copy from 'copy-to-clipboard';
import { toString } from 'lodash';
import { message } from 'antd';
import { formatMessage } from './locales';

const cellResizable = css`
  &::after {
    content: "";
    cursor: col-resize;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 10px;
  }
`;

const headerCellClass = css`
  background-color: var(--header-background-color);
`;

const columnSelectClassname = css`
  background-color: var(--row-hover-background-color);
`;

const columnIsDrop = css`
&:before {
  content: '';
  position: absolute;
  width: calc(100%);
  height: 100%;
  width: 2px;
  background: var(--row-selected-background-color);
  top: 0px;
  left: 0px;
  z-index: 1;
}
`;

const cellResizableClassname = `rdg-cell-resizable ${cellResizable}`;

type SharedHeaderRowProps<R, SR> = Pick<HeaderRowProps<R, SR>,
  | 'onSort'
  | 'allRowsSelected'
>;

export interface HeaderCellProps<R, SR> extends SharedHeaderRowProps<R, SR> {
  column: CalculatedColumn<R, SR>;
  columnIdx: number;
  isCellSelected: boolean;
  sortInfo: SortInfo;
  enableSortRow: boolean;
  cellFilters?: Set<any>;
  filters?: Set<any>;
  onGridSelect: (data: ISelectorEvent) => void;
  onResize: (column: CalculatedColumn<R, SR>, width: number) => void;
  onCellFiltersChange: (columnKey: string, newFilters?: Set<any>) => void;
  onColumnReorder?: (sourceColumnKey: string, targetColumnKey: string) => void;
}
const DRAG_KEY = 'rdg-header-cell'
export default function HeaderCell<R, SR>({
  column,
  columnIdx,
  onResize,
  allRowsSelected,
  isCellSelected,
  cellFilters,
  filters,
  onGridSelect,
  onCellFiltersChange,
  onColumnReorder,
  sortInfo,
  enableSortRow,
  onSort
}: HeaderCellProps<R, SR>) {
  const cellRef = useRef<HTMLDivElement>(null);
  const onColumnClick = useCallback(function (e: React.MouseEvent<HTMLDivElement>) {
    const isFirstColumn = columnIdx === 0;
    onGridSelect({ 
      rowIdx: -1, columnIdx: isFirstColumn ? -1 : columnIdx, isShiftClick: e.shiftKey, isCtrlClick: isCtrlDown(e)
    } as ISelectorEvent);
  }, [column, onGridSelect])

  const onDoubleClick = useCallback(function (e) {
    if (!cellRef.current?.contains(e.target) || column.key === SELECT_COLUMN_KEY) {
      return;
    }
    const name = toString(column.name);
    copy(name);
    message.success(formatMessage("HeaderCell.onDoubleClick.copyName"));
  }, [column, cellRef])

  const drag = useDrag({
    type: DRAG_KEY,
    item: { columnKey: column.key, type: DRAG_KEY },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  const drop = useDrop({
    accept: DRAG_KEY,
    drop({ columnKey, type }: { columnKey: string, type: string }) {
      if (type === DRAG_KEY) {
        onColumnReorder?.(columnKey, column.key);
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'mouse' && event.buttons !== 1) {
      return;
    }

    const { currentTarget, pointerId } = event;
    const { right } = currentTarget.getBoundingClientRect();
    const offset = right - event.clientX;

    if (offset > 11) { // +1px to account for the border size
      return;
    }

    function onPointerMove(event: PointerEvent) {
      if (event.pointerId !== pointerId) return;
      if (event.pointerType === 'mouse' && event.buttons !== 1) {
        onPointerUp(event);
        return;
      }
      const width = event.clientX + offset - currentTarget.getBoundingClientRect().left;
      if (width > 0) {
        onResize(column, width);
      }
    }

    function onPointerUp(event: PointerEvent) {
      if (event.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    }

    event.preventDefault();
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }

  function getCell() {
    if (column.headerRenderer) {
      return (
        <column.headerRenderer
          column={column}
          sortInfo={sortInfo}
          onSort={onSort}
          allRowsSelected={allRowsSelected}
        />
      );
    }

    if (enableSortRow || column.sortable || cellFilters) {
      return (
        <SortableHeaderCell
          column={column}
          onSort={onSort}
          sortInfo={sortInfo}
          enableSortRow={enableSortRow}
          cellFilters={cellFilters}
          checkedFilters={filters}
          onCellFiltersChange={onCellFiltersChange}
        >
          {column.name}
        </SortableHeaderCell>
      );
    }

    return column.name;
  }

  const className = getCellClassname(column, column.headerCellClass, {
    [cellResizableClassname]: column.resizable,
    /**
     * cell 或 column 被选中
     */
    [columnSelectClassname]: isCellSelected,
    [columnIsDrop]: drop[0].isOver
  }, headerCellClass);

  return (
    <div
      role="columnheader"
      aria-colindex={column.idx + 1}
      aria-rowindex={-1}
      className={className}
      style={getCellStyle(column)}
      onPointerDown={column.resizable ? onPointerDown : undefined}
      onClick={onColumnClick}
      onDoubleClick={onDoubleClick}
      ref={onColumnReorder ? useCombinedRefs(drag[1], column.key !== SELECT_COLUMN_KEY ? drop[1] : null, cellRef) : cellRef}
    >
      {getCell()}
    </div>
  );
}
